import { SlidersHorizontal } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import type { Vehicle, VehicleEnergy, VehicleTransmission } from '../../data/vehicles'
import { Button } from '../../ui/Button'
import { Sheet } from '../../ui/Sheet'
import { useInventoryTranslation } from './useInventoryTranslation'
import { useVehicleFactLabels } from './VehicleFacts'

export type InventoryFilterState = {
  make: string
  energy: VehicleEnergy | ''
  transmission: VehicleTransmission | ''
}

export const emptyInventoryFilters: InventoryFilterState = {
  make: '',
  energy: '',
  transmission: '',
}

export function countActiveFilters(filters: InventoryFilterState) {
  return Object.values(filters).filter(Boolean).length
}

function FilterFields({
  vehicles,
  filters,
  onChange,
  onReset,
  mobile = false,
  onApply,
}: {
  vehicles: readonly Vehicle[]
  filters: InventoryFilterState
  onChange: (filters: InventoryFilterState) => void
  onReset: () => void
  mobile?: boolean
  onApply?: () => void
}) {
  const { t } = useInventoryTranslation()
  const factLabels = useVehicleFactLabels()
  const makes = useMemo(() => [...new Set(vehicles.map((vehicle) => vehicle.make))].sort(), [vehicles])
  const energies = useMemo(
    () => [...new Set(vehicles.flatMap((vehicle) => vehicle.facts.energy ? [vehicle.facts.energy] : []))],
    [vehicles],
  )
  const transmissions = useMemo(
    () => [...new Set(vehicles.flatMap((vehicle) => vehicle.facts.transmission ? [vehicle.facts.transmission] : []))],
    [vehicles],
  )

  return (
    <div className="inventory-filters__fields">
      <label>
        <span>{t('filterMake')}</span>
        <select value={filters.make} onChange={(event) => onChange({ ...filters, make: event.target.value })}>
          <option value="">{t('allMakes')}</option>
          {makes.map((make) => <option key={make} value={make}>{make}</option>)}
        </select>
      </label>
      <label>
        <span>{t('filterEnergy')}</span>
        <select
          value={filters.energy}
          onChange={(event) => onChange({ ...filters, energy: event.target.value as VehicleEnergy | '' })}
        >
          <option value="">{t('allEnergies')}</option>
          {energies.map((energy) => <option key={energy} value={energy}>{factLabels.energy(energy)}</option>)}
        </select>
      </label>
      <label>
        <span>{t('filterTransmission')}</span>
        <select
          value={filters.transmission}
          onChange={(event) => onChange({ ...filters, transmission: event.target.value as VehicleTransmission | '' })}
        >
          <option value="">{t('allTransmissions')}</option>
          {transmissions.map((transmission) => (
            <option key={transmission} value={transmission}>{factLabels.transmission(transmission)}</option>
          ))}
        </select>
      </label>
      <div className="inventory-filters__actions">
        <Button type="button" variant="quiet" onClick={onReset}>{t('filterReset')}</Button>
        {mobile ? <Button type="button" onClick={onApply}>{t('filterApply')}</Button> : null}
      </div>
    </div>
  )
}

export function InventoryFilters({
  vehicles,
  filters,
  onChange,
  onReset,
}: {
  vehicles: readonly Vehicle[]
  filters: InventoryFilterState
  onChange: (filters: InventoryFilterState) => void
  onReset: () => void
}) {
  const { t } = useInventoryTranslation()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const activeCount = countActiveFilters(filters)
  const activeLabel = t(activeCount === 1 ? 'filterActive_one' : 'filterActive_other', { count: activeCount })

  return (
    <>
      <aside className="inventory-filters inventory-filters--desktop" aria-labelledby="desktop-filter-title">
        <div className="inventory-filters__heading">
          <h2 id="desktop-filter-title">{t('filterTitle')}</h2>
          <p>{t('filterDescription')}</p>
        </div>
        <FilterFields vehicles={vehicles} filters={filters} onChange={onChange} onReset={onReset} />
      </aside>

      <Button
        ref={triggerRef}
        className="inventory-filter-trigger"
        type="button"
        variant="secondary"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <SlidersHorizontal aria-hidden="true" />
        {t('filterOpen')}
        {activeCount ? <span className="inventory-filter-trigger__count">{activeLabel}</span> : null}
      </Button>

      <Sheet
        open={open}
        onOpenChange={setOpen}
        title={t('filterTitle')}
        description={t('filterDescription')}
        closeLabel={t('filterClose')}
        triggerRef={triggerRef}
        className="inventory-filter-sheet"
      >
        <FilterFields
          vehicles={vehicles}
          filters={filters}
          onChange={onChange}
          onReset={onReset}
          mobile
          onApply={() => setOpen(false)}
        />
      </Sheet>
    </>
  )
}
